<template>
  <MenuList class="w-[280px]">
    <MenuListItem
      icon="Share"
      :disabled="isFileInactive"
      @click="handleAction(handleShareClick)"
    >
      Share
    </MenuListItem>
    <MenuListItem
      icon="Send"
      :disabled="isFileInactive"
      @click="handleAction(handleInviteClick)"
    >
      Invite
    </MenuListItem>
    <MenuListItem
      icon="Download"
      :disabled="!canDownload"
      @click="handleAction(handleDownloadClick)"
    >
      Download
    </MenuListItem>

    <MenuListSeparator />

    <MenuListItem
      icon="Settings"
      @click="handleAction(handleFileSettingsClick)"
    >
      Copyright settings
    </MenuListItem>

    <MenuListItem
      :disabled="isFileInactive"
      icon="Lock"
      @click="handleAction(handleFilePrivacySettingsClick)"
    >
      Privacy controls
    </MenuListItem>

    <MenuListItem icon="Move" @click="handleAction(handleMoveClick)">
      Move
    </MenuListItem>

    <MenuListItem
      class="text-red-600"
      @click="handleAction(handleArchiveClick)"
    >
      <Icon name="Archive" :size="18" class="mr-3 stroke-red-600" />
      Archive
    </MenuListItem>

    <MenuListSeparator />

    <MenuListItem
      class="stroke-none!"
      icon="Dacr"
      :disabled="isFileInactive || !certificateUrl"
      @click="handleViewCertificateClick"
    >
      View Dacr Certificate
    </MenuListItem>

    <MenuListSeparator />

    <MenuListItem icon="Coin" disabled>
      <div class="font-secondary">Sell / Transfer</div>
      <template #right>
        <Pill>Coming soon</Pill>
      </template>
    </MenuListItem>

    <MenuListItem icon="License" disabled>
      <div class="font-secondary">License</div>
      <template #right>
        <Pill>Coming soon</Pill>
      </template>
    </MenuListItem>
  </MenuList>
</template>

<script setup lang="ts">
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import {
  CopyrightStatus,
  FilePermission,
  type File,
} from '@/shared/graphql/router/graphql'
import { computed } from 'vue'
import {
  MenuList,
  MenuListItem,
  MenuListSeparator,
} from '@/shared/components/Menu'
import { Icon, Pill } from '@dacr/ui'

const { file } = defineProps<{
  file: File
}>()
const emit = defineEmits<{
  clicked: []
}>()

const { open } = useDialog()

const isFileInactive = computed(
  () => file.copyright?.status === CopyrightStatus.Inactive
)

const canDownload = computed(() =>
  file.permissions.includes(FilePermission.Download)
)

const certificateUrl = computed(
  () => file.copyright?.registrations?.[0]?.certificateUrl
)

const handleAction = (fn: () => void) => {
  fn()
  emit('clicked')
}

const handleShareClick = () => {
  open(Dialogs.Share, { props: { file } })
}

const handleInviteClick = () => {
  open(Dialogs.FileAccess, { props: { file } })
}

const handleDownloadClick = () => {
  open(Dialogs.Download, { props: { file } })
}

const handleFileSettingsClick = () => {
  open(Dialogs.FileSettings, { props: { file } })
}

const handleFilePrivacySettingsClick = () => {
  open(Dialogs.FilePrivacySettings, { props: { file } })
}

const handleArchiveClick = () => {
  open(Dialogs.ArchiveFiles, { props: { files: [file] } })
}

const handleMoveClick = () => {
  open(Dialogs.MoveItems, { props: { ids: [file.id] } })
}

const handleViewCertificateClick = () => {
  if (!certificateUrl.value) return
  window.open(certificateUrl.value, '_blank')
  emit('clicked')
}
</script>
